import { useMemo } from 'react';
import { Object3D } from 'three';
import {
  useDataContext,
  useEventContext,
  useFilterContext,
  useModeContext,
  useTwinContext,
} from '@repo/front-end-state';
import { activeSceneBehaviourFor, isArealessSceneMode } from '@repo/front-end-utils';
import {
  buildInScopeColorModes,
  buildHeatmapValues,
  HeatmapFloorView,
  type InScopeColorMode,
} from '@repo/ui';

interface Props {
  /**
   * Scene subtree to scope entity collection to. Omitted on the single-floor site-level
   * mount (whole scene). On a multifloor twin the per-floor `Floor` passes its own altitude
   * `<group>`, so the plane is coloured only by that floor's entities.
   */
  root?: Object3D;
}

/** Stable empty map so the non-heatmap path never churns the memo identity. */
const EMPTY_COLOR_MODES = new Map<string, InScopeColorMode>();

/**
 * R7 heatmap floor — the per-app, context-consuming wrapper (thin, like `Box`).
 * Reads the selected hero metric + mode + data, gates on the AdminDB-configured
 * scene behaviour, builds the per-entity value map, and delegates all rendering
 * to the shared presentational `HeatmapFloorView`. Renders nothing unless the
 * current (metric, mode) config is `HEATMAP` — so box behaviour is untouched by
 * default.
 */
export const HeatmapFloor = ({ root }: Props) => {
  const { heroMetric, filter } = useFilterContext();
  const { appMode } = useModeContext();
  const { data } = useDataContext();
  const { twin, multifloor, hasFloorLevel } = useTwinContext();
  const { selectedEntityIds } = useEventContext();

  const isHeatmap = activeSceneBehaviourFor(heroMetric, appMode) === 'heatmap';
  const arealess = isArealessSceneMode(appMode);
  // Only the arealess (Equipment) branch consumes the derived selection; in areal modes this is a
  // stable `undefined`, so the colorModes memo below doesn't re-walk the tree when EventContext
  // re-dispatches `selectedEntityIds` on an unrelated ENTITY-path/filter change.
  const arealessSelectedIds = arealess ? selectedEntityIds : undefined;

  const values = useMemo(
    () =>
      buildHeatmapValues(
        heroMetric ? data.processed?.[heroMetric.valueMetricName] : undefined,
        heroMetric ? data.processed?.[heroMetric.indicatorMetricName] : undefined,
      ),
    [data.processed, heroMetric],
  );

  // The in-scope map: which entities the field samples AND their colour mode, computed by the
  // shared resolver from the SAME primitives Box's render path uses — so the floor scopes and
  // greys exactly where the boxes do. In arealess (Equipment) mode the scope is all assets and the
  // colour split follows the mode's derived selection (`selectedEntityIds`). Recomputed only on the
  // box-greying cadence (twin/filter/selection/mode); skipped entirely when the heatmap isn't the
  // active behaviour (this component self-gates to null below).
  const colorModes = useMemo(
    () =>
      isHeatmap
        ? buildInScopeColorModes(twin?.physicalModel, filter, {
            hasFloorLevel,
            multifloor,
            arealess,
            arealessSelectedIds,
          })
        : EMPTY_COLOR_MODES,
    [isHeatmap, twin, filter, hasFloorLevel, multifloor, arealess, arealessSelectedIds],
  );

  if (!isHeatmap || !heroMetric) return null;

  return (
    <HeatmapFloorView
      values={values}
      indicatorConfig={heroMetric.indicatorConfig}
      metricName={heroMetric.name}
      colorModes={colorModes}
      root={root}
    />
  );
};
